/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-unused-expressions */
import React, { CSSProperties, useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { List, AutoSizer, CellMeasurer, CellMeasurerCache } from 'react-virtualized'
import _ from 'lodash'

type CheckBoxSelectProps<T extends Record<string, any>> = {
    options: TypeCheckBoxSelectOption<T>[]
    onChange: (newOption: TypeCheckBoxSelectOption<T>, options: TypeCheckBoxSelectOption<T>[]) => void
    valueRenderer?: (option: TypeCheckBoxSelectOption<any>) => React.ReactNode
    loadOptions?: (callBack: (value: React.SetStateAction<TypeCheckBoxSelectOption<T>[]>) => void) => void
    style?: CSSProperties
    async?: boolean
    onScroll?: (obj: {}) => void
    loading?: boolean
    filters?: TypeFilter<T>
    setCheckedOptions?: (checkedOptions: TypeCheckBoxSelectOption<T>[]) => void
    filterFn?: (option: TypeCheckBoxSelectOption<T>, filters: TypeFilter<T>) => boolean
    onScrollBottom: (loading: boolean) => void
    onScrollTop?: (loading: boolean) => void
    onUpdateFilters?: (newFilters: TypeFilter<T>) => void
    onNoScroll?: (
        loading: boolean,
        obj: {
            overscanStartIndex: number
            overscanStopIndex: number
            startIndex: number
            stopIndex: number
        }
    ) => void
    __debouce_time__?: number
}

export type TypeFilter<T = { [k: string]: any }> =
    | Record<
          keyof T,
          {
              value?: (string | number | boolean)[]
              label?: string
              checked?: boolean
          }
      >
    | {
          [k: string]: {
              label?: string
              [k: string]: any
          }
      }

export type TypeCheckBoxSelectOption<T = Record<string, any>> = {
    label: string
    value: string | number
    data: T
    defaultValue?: boolean
    checked: boolean
    [k: string]: unknown
}

function CheckBoxSelect<A extends Record<string, any>>(props: CheckBoxSelectProps<A>) {
    const {
        options,
        onChange,
        valueRenderer,
        loadOptions,
        style,
        async,
        onScroll,
        filters,
        filterFn,
        setCheckedOptions,
        onNoScroll,
        onScrollBottom,
        onScrollTop,
        onUpdateFilters,
        __debouce_time__
    } = props

    const [loading, setLoading] = useState(false)
    const [option, setOption] = useState(options)
    const [filtersState, setFilters] = useState(filters)
    const [lastSrollTop, setLastSrollTop] = useState(0)

    const handleClick = (value: TypeCheckBoxSelectOption<A>, index: number): void => {
        const newOptions: TypeCheckBoxSelectOption<A>[] = Object.assign([], option)
        newOptions[index].checked = !newOptions[index].checked

        onChange?.(value, newOptions)
        setOption(newOptions)
    }

    const cache = useRef(
        new CellMeasurerCache({
            fixedWidth: true,
            defaultHeight: 100
        })
    )

    useEffect(() => {
        onUpdateFilters(filters)
        if (async) {
            setLoading(true)
            loadOptions(setOption)
            setLoading(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        if (filters) {
            const filteredOptions = options.filter(opt =>
                filterFn(
                    opt,
                    Object.keys(filtersState).reduce(
                        (p, c) => ({
                            ...p,
                            [c]: {
                                ...filtersState[c],
                                checked: filtersState[c].checked ?? filtersState[c].defaultValue
                            }
                        }),
                        {} as TypeFilter<A>
                    )
                )
            )

            setOption(filteredOptions)
            setCheckedOptions && setCheckedOptions(filteredOptions.filter(opt => opt.checked))
        }
    }, [filtersState, options, filters])

    useEffect(() => {
        setLoading(props.loading)
    }, [props.loading])

    const handleScroll = obj => {
        onScroll?.(obj)
        const top = obj.scrollTop
        if (top <= 0) {
            _.debounce(() => onScrollTop?.(loading), __debouce_time__ || 500, {
                leading: true,
                trailing: false
            })()
        }
        if (top + obj.clientHeight - obj.scrollHeight >= 0 && lastSrollTop < top) {
            _.debounce(() => onScrollBottom?.(loading), __debouce_time__ || 500, {
                leading: true,
                trailing: false
            })()
        }

        setLastSrollTop(top)
    }

    return (
        <CheckBoxSelectContainer style={style}>
            {filters && (
                <CheckBoxSelectHeader
                    filters={filtersState}
                    onChange={newFilters => {
                        setFilters(newFilters)
                        onUpdateFilters(newFilters)
                    }}
                />
            )}
            {option && (
                <AutoSizer>
                    {({ width, height }) => (
                        <List
                            onRowsRendered={obj => {
                                if (obj.overscanStopIndex - 1 <= 0) {
                                    onNoScroll(loading, obj)
                                }
                            }}
                            onScroll={handleScroll}
                            width={width}
                            height={height}
                            rowHeight={cache.current.rowHeight}
                            deferredMeasurementCache={cache.current}
                            rowCount={option.length}
                            rowRenderer={({ key, index, style: _style, parent }) => {
                                const opt = option[index]

                                return (
                                    <CellMeasurer
                                        key={key}
                                        cache={cache.current}
                                        parent={parent}
                                        columnIndex={0}
                                        rowIndex={index}
                                    >
                                        <CheckBoxSelectOption
                                            bg={opt.checked && 'rgba(0, 126, 255, 0.06)'}
                                            key={opt.value}
                                            style={_style}
                                            onClick={() => handleClick(opt, index)}
                                        >
                                            <input type="checkbox" checked={opt.checked} />
                                            {valueRenderer ? (
                                                valueRenderer(opt)
                                            ) : (
                                                <div>
                                                    <label>{opt.label}</label>
                                                </div>
                                            )}
                                        </CheckBoxSelectOption>
                                    </CellMeasurer>
                                )
                            }}
                        />
                    )}
                </AutoSizer>
            )}
        </CheckBoxSelectContainer>
    )
}

type CheckBoxSelectHeaderProps = {
    filters?: TypeFilter

    onChange?: (updatedFilters: TypeFilter) => void
}
export const CheckBoxSelectHeader: React.FC<CheckBoxSelectHeaderProps> = (props: CheckBoxSelectHeaderProps) => {
    const { filters, onChange } = props
    const [_filters, setFilters] = useState(filters)

    const handleClick = (key: string, value: [string | number | boolean], checked: boolean): void => {
        const updatedFilters = { ..._filters, [key]: { ..._filters[key], checked: !checked } }
        onChange(updatedFilters)
        setFilters(updatedFilters)
    }

    return (
        <CheckBoxSelectHeaderContainer>
            {Object.keys(_filters).map((key, index) => (
                <div
                    key={key}
                    onClick={() =>
                        handleClick(key, _filters[key].value, _filters[key].checked ?? _filters[key].defaultValue)
                    }
                >
                    <input type="checkbox" name={key} checked={_filters[key].checked ?? _filters[key].defaultValue} />
                    <label htmlFor={key}>{_filters[key].label || key}</label>
                </div>
            ))}
        </CheckBoxSelectHeaderContainer>
    )
}

const CheckBoxSelectHeaderContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 7px 16px;
    background-color: #eee;
    border-right: solid 1px #bac0cb;
    border-left: solid 1px #bac0cb;
    border-top: solid 1px #bac0cb;

    div {
        display: flex;
        margin-right: 10px;
        flex-direction: row;
        font-weight: normal;
        input {
            margin-right: 5px;
        }
    }
`

const CheckBoxSelectContainer = styled.div`
    display: flex;
    flex-direction: column;
    height: 200px;
    .ReactVirtualized__Grid.ReactVirtualized__List {
        border: solid 1px #bac0cb;
    }
`
const CheckBoxSelectOption = styled.div<{ bg: string }>`
    display: flex;
    height: 100%;
    padding: 10px 16px;
    z-index: 10;
    border-bottom: solid 1px #bac0cb;

    ${({ bg }) => bg && `background-color: ${bg};`}
    input {
        margin-right: 10px;
    }
    :hover {
        background-color: rgba(0, 126, 255, 0.04);
    }
`

export default CheckBoxSelect
